import { CONTACT } from "../../lib/contact";
import { useT } from "../../i18n/LanguageContext";

export default function Contact() {
    const { t } = useT();
    const monthName = new Date().toLocaleString(t("contact.localeTag"), {
        month: "long",
    });

    return (
        <section
            id="contact"
            data-testid="contact-section"
            data-cta-source="contact"
            className="relative overflow-hidden border-t border-warm"
        >
            <div
                className="absolute inset-0 -z-10"
                style={{
                    backgroundImage: 'url("/contact-bg.webp")',
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
                aria-hidden
            />
            <div className="absolute inset-0 -z-10 bg-[var(--sand)]/82" aria-hidden />

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-28 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-7 reveal">
                    <p className="overline mb-6">{t("contact.overline")}</p>
                    <h2 className="font-serif text-5xl sm:text-6xl md:text-7xl tracking-tighter leading-[1.02] text-ink">
                        {t("contact.headlineA")}{" "}
                        <em className="italic font-light text-terracotta">
                            {t("contact.headlineEm")}
                        </em>{" "}
                        {t("contact.headlineB")}
                    </h2>
                    <p className="mt-8 max-w-xl text-lg leading-relaxed text-secondary">
                        {t("contact.intro")}
                    </p>

                    <div className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={CONTACT.calendlyHref}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-calendly-button"
                            className="inline-flex items-center gap-3 bg-ink text-sand px-7 py-4 text-sm hover:bg-[var(--ink-soft)] transition-colors"
                        >
                            {t("contact.ctaCall")} →
                        </a>
                        <a
                            href={CONTACT.whatsappHref}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="contact-whatsapp-button"
                            className="inline-flex items-center gap-3 border border-ink text-ink px-7 py-4 text-sm hover:bg-ink hover:text-sand transition-colors"
                        >
                            {t("contact.ctaWhatsapp")}
                        </a>
                    </div>
                </div>

                <aside className="lg:col-span-5 reveal lg:pl-10">
                    <div className="border-t border-warm pt-8 space-y-6">
                        <Row
                            label={t("contact.rowWhatsapp")}
                            value={CONTACT.whatsappNumber}
                            href={CONTACT.whatsappHref}
                            testid="contact-row-whatsapp"
                        />
                        <Row
                            label={t("contact.rowCalendly")}
                            value="calendly.com/scalater"
                            href={CONTACT.calendlyHref}
                            testid="contact-row-calendly"
                        />
                        <Row
                            label={t("contact.rowEmail")}
                            value={CONTACT.email}
                            href={CONTACT.emailHref}
                            testid="contact-row-email"
                        />
                        <Row
                            label={t("contact.rowLocation")}
                            value={CONTACT.location}
                            testid="contact-row-location"
                        />
                        <Row
                            label={t("contact.rowAvailability")}
                            value={`${t("contact.availabilityValue")} · ${monthName}`}
                            testid="contact-row-availability"
                            accent
                        />
                    </div>
                </aside>
            </div>
        </section>
    );
}

const Row = ({ label, value, href, accent, testid }) => (
    <div className="flex items-baseline justify-between gap-6">
        <span className="text-xs uppercase tracking-[0.2em] text-secondary">
            {label}
        </span>
        {href ? (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={testid}
                className={`font-serif text-lg md:text-xl link-underline ${
                    accent ? "text-terracotta" : "text-ink"
                }`}
            >
                {value}
            </a>
        ) : (
            <span
                data-testid={testid}
                className={`font-serif text-lg md:text-xl ${
                    accent ? "text-terracotta" : "text-ink"
                }`}
            >
                {value}
            </span>
        )}
    </div>
);
